import { randomUUID } from 'crypto';

export interface DomainModelElement {
  id: string;
  name: string;
  description?: string;
}

export interface BoundedContextProperties {
  domainId: string;
  name: string;
  description?: string;
  type: 'core' | 'supporting' | 'generic';
  ubiquitousLanguage: Record<string, string>;
  domainServices: DomainModelElement[];
  aggregates: DomainModelElement[];
  valueObjects: DomainModelElement[];
  entities: DomainModelElement[];
}

export class BoundedContext {
  readonly id: string;
  readonly createdAt: Date;
  private _updatedAt: Date;
  private _domainId: string;
  private _name: string;
  private _description?: string;
  private _type: 'core' | 'supporting' | 'generic';
  private _ubiquitousLanguage: Record<string, string>;
  private _domainServices: DomainModelElement[];
  private _aggregates: DomainModelElement[];
  private _valueObjects: DomainModelElement[];
  private _entities: DomainModelElement[];

  private constructor(properties: BoundedContextProperties, id?: string) {
    this.id = id || randomUUID();
    this.createdAt = new Date();
    this._updatedAt = new Date();
    this._domainId = properties.domainId;
    this._name = properties.name;
    this._description = properties.description;
    this._type = properties.type;
    this._ubiquitousLanguage = properties.ubiquitousLanguage;
    this._domainServices = properties.domainServices;
    this._aggregates = properties.aggregates;
    this._valueObjects = properties.valueObjects;
    this._entities = properties.entities;

    this.validateInvariants();
  }

  static create(properties: BoundedContextProperties): BoundedContext {
    return new BoundedContext(properties);
  }

  static reconstitute(
    properties: BoundedContextProperties,
    id: string,
    createdAt: Date,
    updatedAt: Date
  ): BoundedContext {
    const context = new BoundedContext(properties, id);
    (context as any).createdAt = createdAt;
    context._updatedAt = updatedAt;
    return context;
  }

  get domainId(): string {
    return this._domainId;
  }

  get name(): string {
    return this._name;
  }

  get description(): string | undefined {
    return this._description;
  }

  get type(): 'core' | 'supporting' | 'generic' {
    return this._type;
  }

  get ubiquitousLanguage(): Record<string, string> {
    return { ...this._ubiquitousLanguage };
  }

  get domainServices(): DomainModelElement[] {
    return [...this._domainServices];
  }

  get aggregates(): DomainModelElement[] {
    return [...this._aggregates];
  }

  get valueObjects(): DomainModelElement[] {
    return [...this._valueObjects];
  }

  get entities(): DomainModelElement[] {
    return [...this._entities];
  }

  get updatedAt(): Date {
    return this._updatedAt;
  }

  updateName(name: string): void {
    if (!name || name.trim().length === 0) {
      throw new Error('Bounded context name cannot be empty');
    }
    this._name = name.trim();
    this.touch();
  }

  updateDescription(description?: string): void {
    this._description = description?.trim() || undefined;
    this.touch();
  }

  updateType(type: 'core' | 'supporting' | 'generic'): void {
    this._type = type;
    this.touch();
  }

  addUbiquitousLanguageTerm(term: string, definition: string): void {
    if (!term || !definition) {
      throw new Error('Term and definition are required');
    }
    this._ubiquitousLanguage[term.trim()] = definition.trim();
    this.touch();
  }

  removeUbiquitousLanguageTerm(term: string): void {
    delete this._ubiquitousLanguage[term];
    this.touch();
  }

  addDomainService(service: DomainModelElement): void {
    if (!service.name) {
      throw new Error('Service name is required');
    }
    if (!this._domainServices.find(s => s.id === service.id)) {
      this._domainServices.push(service);
      this.touch();
    }
  }

  removeDomainService(serviceId: string): void {
    const index = this._domainServices.findIndex(s => s.id === serviceId);
    if (index > -1) {
      this._domainServices.splice(index, 1);
      this.touch();
    }
  }

  addAggregate(aggregate: DomainModelElement): void {
    if (!aggregate.name) {
      throw new Error('Aggregate name is required');
    }
    if (!this._aggregates.find(a => a.id === aggregate.id)) {
      this._aggregates.push(aggregate);
      this.touch();
    }
  }

  removeAggregate(aggregateId: string): void {
    const index = this._aggregates.findIndex(a => a.id === aggregateId);
    if (index > -1) {
      this._aggregates.splice(index, 1);
      this.touch();
    }
  }

  addValueObject(valueObject: DomainModelElement): void {
    if (!valueObject.name) {
      throw new Error('Value object name is required');
    }
    if (!this._valueObjects.find(v => v.id === valueObject.id)) {
      this._valueObjects.push(valueObject);
      this.touch();
    }
  }

  removeValueObject(valueObjectId: string): void {
    const index = this._valueObjects.findIndex(v => v.id === valueObjectId);
    if (index > -1) {
      this._valueObjects.splice(index, 1);
      this.touch();
    }
  }

  addEntity(entity: DomainModelElement): void {
    if (!entity.name) {
      throw new Error('Entity name is required');
    }
    if (!this._entities.find(e => e.id === entity.id)) {
      this._entities.push(entity);
      this.touch();
    }
  }

  removeEntity(entityId: string): void {
    const index = this._entities.findIndex(e => e.id === entityId);
    if (index > -1) {
      this._entities.splice(index, 1);
      this.touch();
    }
  }

  private touch(): void {
    this._updatedAt = new Date();
  }

  private validateInvariants(): void {
    if (!this._domainId) {
      throw new Error('Domain ID is required');
    }

    if (!this._name || this._name.trim().length === 0) {
      throw new Error('Bounded context name is required');
    }

    if (this._name.length > 100) {
      throw new Error('Bounded context name must be 100 characters or less');
    }

    if (this._description && this._description.length > 1000) {
      throw new Error('Bounded context description must be 1000 characters or less');
    }

    // Accept any string type - following Published Language pattern
    // Domain validation can happen elsewhere if needed
  }

  toJSON() {
    return {
      id: this.id,
      domainId: this._domainId,
      name: this._name,
      description: this._description,
      type: this._type,
      ubiquitousLanguage: this._ubiquitousLanguage,
      domainServices: this._domainServices,
      aggregates: this._aggregates,
      valueObjects: this._valueObjects,
      entities: this._entities,
      createdAt: this.createdAt,
      updatedAt: this._updatedAt
    };
  }
}